import {
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';
import {I18n} from 'aws-amplify/utils';

import * as DnsRecordsService from '../../dns-records/services/DnsRecordsService';
import {
    ApplyDnsRecordChangesInput,
    DnsWriteResult,
} from '../../dns-records/types';
import {dnsRecordsKeys} from '../../dns-records/hooks/useDnsRecordsQuery';
import {zonesKeys} from './useZonesQuery';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';
import {classifyWriteError} from '../services/writeErrors';
import {writeErrorCopy} from '../writeErrorMessages';
import {DnsRecordChangeInput} from '../../dns-records/types';

// FNP-1466 — WRITING THE DELEGATION.
//
// ⚠️ THE RECORD IS NAMED FOR THE CHILD AND WRITTEN INTO THE PARENT'S ZONE,
// THROUGH THE PARENT'S CONNECTION. All three halves of that come from different
// places, which is exactly why doing it by hand was error-prone: the operator
// had to hold the child's name and nameservers in their head while navigating
// to a different zone in a different account.
//
// ⚠️ NO NEW MUTATION. This is `applyDnsRecordChanges`, the same one every other
// record write uses, with a record `services/delegation.ts` derived. Adding a
// `delegateDnsZone` mutation would be a second path to one provider call, and
// the API would then have two ways to write an NS record that could diverge.

/**
 * Write the NS record that delegates a zone from its parent.
 *
 * The caller supplies the PARENT's connection and zone id — the operator chose
 * them — and the record, which `delegationRecordFor` built from the child.
 */
export const useDelegateZoneMutation = () => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: {
            parentConnectionId: string,
            parentZoneId: string,
            record: DnsRecordChangeInput,
            // Carried only so the success message can name what was delegated.
            childZoneName: string,
            // Likewise, for the removal message — "dev.flexi.network no longer
            // points at it" is clearer than "the parent zone".
            parentZoneName?: string,
        }): Promise<DnsWriteResult | undefined> => {

            const input: ApplyDnsRecordChangesInput = {
                connectionId: args.parentConnectionId,
                zoneId: args.parentZoneId,
                changes: [args.record],
            };

            return await DnsRecordsService.applyRecordChanges(input);
        },
        onSuccess: (result, args) => {

            // ⚠️ THE WRITE STATE IS NOT A CLAIM THAT DNS IS LIVE, and a
            // delegation is the case where that matters most: an NS record is
            // cached for its TTL, so "accepted by the provider" and "the world
            // can see it" are hours apart. The message says what was observed
            // and no more.
            //
            // ⚠️ AND IT NEVER SAYS THE ZONE IS NOW DELEGATED. That is the
            // delegation CHECK's answer, read from a resolver, not something
            // this write can assert — see FNP-1483 for what happens when a
            // write path claims an outcome it did not observe.
            const settled = result?.writeState === 'SETTLED';

            // ⚠️ A REMOVAL MUST NOT REPORT AS A WRITE. The same mutation
            // carries both, and telling an operator their delegation was
            // "written" when they just removed it is the class of defect
            // FNP-1483 is open for — a write path claiming an outcome that did
            // not happen.
            const removed = args.record.action === 'DELETE';

            const message = removed
                ? (settled
                    ? I18n.get('Delegation removed for {zone}. {parent} no longer points at it; resolvers may serve the old answer until the record\'s time to live expires.')
                    : I18n.get('Delegation removal accepted for {zone}. The provider has not confirmed it is applied yet.'))
                : (settled
                    ? I18n.get('Delegation record written for {zone}. It is live on the provider\'s nameservers; resolvers elsewhere may still be serving the old answer.')
                    : I18n.get('Delegation record accepted for {zone}. The provider has not confirmed it is live yet.'));

            showSuccessNotification(
                message
                    .split('{zone}').join(args.childZoneName)
                    .split('{parent}').join(args.parentZoneName ?? I18n.get('The parent zone')),
            );

            // The record now exists in the parent zone, so its record lists are
            // stale.
            queryClient.invalidateQueries({queryKey: dnsRecordsKeys.lists()});

            // ⚠️ AND THE ZONES LIST, because the child's delegation STATUS is
            // what the operator is about to look at. It is a resolver read and
            // may still answer NOT_DELEGATED for a while — that is DNS, not a
            // bug — but leaving the cached row untouched means it cannot ever
            // update without a manual Reload, which is FNP-1482.
            //
            // ⚠️ `refetchType: 'none'` SO THE OPEN DRAWER DOES NOT REMOUNT.
            // The drawer reads the zones list to resolve the parent, so
            // invalidating it with an immediate refetch re-renders the drawer
            // mid-operation and wipes the operator's connection choice — a
            // second click then does nothing because the picker has silently
            // reset. Marking the data stale is enough: the list refetches when
            // the operator returns to it, which is when they look at it.
            queryClient.invalidateQueries({
                queryKey: zonesKeys.all,
                refetchType: 'none',
            });
        },
        onError: (e: unknown) => {
            const classified = classifyWriteError(e);
            const copy = writeErrorCopy(classified.kind);
            showErrorNotification(`${copy.title} ${copy.action}`);
        },
    });
};
